import {Dispatch, useContext, useReducer, useState} from 'react';
import {
  GET_NSC_CATALOG_ITEM,
  NSC_ITEM_WITH_BUYER_AND_SUBS,
} from '@buncha/config/graphQL/documentNode/queries';
import {
  GetNSCCatalogItemForShopperResponse,
  GetNSCItemWithBuyerAndSubsResponse,
  NSCBuyer,
  NSCItem,
  State,
  UpdateNSCItem,
} from '../types/NSCItemScanner';
import {NSCItemScannerContext, initialState} from '../context/NSCItemScanner';
import {PayloadAction, createSlice} from '@reduxjs/toolkit';

import {OrderItemStatus} from '@buncha/common/types/common';
import {ToastType} from '@buncha/components/composites/notification/type';
import {UPDATE_NSC_ITEM_STATUS} from '@buncha/config/graphQL/documentNode/mutation';
import {gqlService} from '@buncha/config/graphQL';
import {useToastMessage} from '@buncha/components/composites/notification';
import {useUpdateItemDetails} from './ItemScanner';

const reducer = {
  setItemData: function (state: State, action: PayloadAction<NSCItem>) {
    state.item = action.payload;
  },
  setBuyersData: function (state: State, action: PayloadAction<NSCBuyer[]>) {
    state.buyers = action.payload;
  },
  addBarcode: function (state: State, action: PayloadAction<string>) {
    state.barcodes.push(action.payload);
  },
  removeBarcode: function (state: State, action: PayloadAction<string>) {
    const i = state.barcodes.findIndex(el => el === action.payload);
    if (i !== -1)
      state.barcodes = [
        ...state.barcodes.slice(0, i),
        ...state.barcodes.slice(i + 1),
      ];
  },
  clearBarcodes: function (state: State) {
    state.barcodes = [];
  },
};

const slice = createSlice({
  initialState,
  name: 'NSCItemScannerContext',
  reducers: reducer,
});

export function useNSCItemScannerReducer() {
  const reducerInfo = useReducer(slice.reducer, initialState);
  return reducerInfo;
}

export function useNSCItemScannerContext() {
  return useContext(NSCItemScannerContext);
}

export const NSCItemScannerAction = slice.actions;

export function useNSCItemDetails(dispatch: Dispatch<any> | null) {
  const [itemLoading, setItemLoading] = useState(false);
  const [showErrorToast] = useToastMessage(ToastType.Error);

  const getNSCItemDetails = async (
    itemId: number,
    runId: number,
    storeIndex: number,
  ) => {
    try {
      setItemLoading(true);
      const response =
        await gqlService?.query<GetNSCCatalogItemForShopperResponse>({
          query: GET_NSC_CATALOG_ITEM,
          variables: {itemId, runId, storeIndex},
          fetchPolicy: 'network-only',
        });
      if (response?.data?.getCatalogItem)
        dispatch?.(
          NSCItemScannerAction.setItemData(response.data.getCatalogItem),
        );
    } catch (error: any) {
      showErrorToast(error?.message);
    } finally {
      setItemLoading(false);
    }
  };

  return {getNSCItemDetails, itemLoading};
}

export function useNSCBuyersDetails(dispatch: Dispatch<any> | null) {
  const [buyersLoading, setBuyersLoading] = useState(false);
  const [showErrorToast] = useToastMessage(ToastType.Error);

  const getNSCBuyers = async (
    catalogItemId: number,
    runId: number,
    orderItemId: number,
  ) => {
    try {
      setBuyersLoading(true);
      const response =
        await gqlService?.query<GetNSCItemWithBuyerAndSubsResponse>({
          query: NSC_ITEM_WITH_BUYER_AND_SUBS,
          variables: {catalogItemId, runId, orderItemId},
          fetchPolicy: 'network-only',
        });
      if (response?.data?.itemWithBuyersAndSub?.length)
        dispatch?.(
          NSCItemScannerAction.setBuyersData(
            response.data.itemWithBuyersAndSub ?? [],
          ),
        );
      return response?.data?.itemWithBuyersAndSub?.length;
    } catch (error: any) {
      showErrorToast(error?.message);
    } finally {
      setBuyersLoading(false);
    }
  };

  return {getNSCBuyers, buyersLoading};
}

export function usePickNSCItem(dispatch: Dispatch<any> | null) {
  const {updateItemDetails} = useUpdateItemDetails();
  const [pickNSCItemLoading, setPickNSCItemLoading] = useState(false);
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const {getNSCBuyers} = useNSCBuyersDetails(dispatch);

  const pickNSCItem = async (
    orderItemId: number,
    runId: number,
    catalogItemid: number,
    actualUpcs: string[],
    status?: OrderItemStatus,
  ) => {
    try {
      setPickNSCItemLoading(true);
      const res = await gqlService?.mutation<UpdateNSCItem>({
        mutation: UPDATE_NSC_ITEM_STATUS,
        variables: {
          orderItemId,
          runId,
          actualUpcs,
          orderItemStatus: status ?? OrderItemStatus.picked,
        },
      });
      const item = res?.data?.updateOrderItemStatus;
      dispatch?.(NSCItemScannerAction.clearBarcodes());
      if (res)
        updateItemDetails(
          [
            {
              orderItemId,
              price: item?.price,
              quantity: item?.quantity,
              weightInfo: [
                {
                  unit: item?.orderItemAttribute?.units,
                  weight: item?.orderItemAttribute?.weight,
                },
              ],
            },
          ],
          false,
          undefined,
          true,
        );
      return await getNSCBuyers(catalogItemid, runId, orderItemId);
    } catch (error: any) {
      showErrorToast(error?.message);
    } finally {
      setPickNSCItemLoading(false);
    }
  };

  return {pickNSCItem, pickNSCItemLoading};
}
